import { useEffect, useState } from "react";
import {
  API_SETTLEMENT_BENEFICIARIES,
  API_SETTLEMENT_ELIGIBILITY,
} from "../../../utils/apis";
import { BENEFICIARY_TYPES } from "../../../utils/constants";
import useGet from "../../common/useGet";
import { Modal, Form, Select, DatePicker, Button, Row, Col } from "antd";
import { useGlobalContext } from "../../../context/GlobalContext";
import useSet from "../../common/useSet";
import toast from "react-hot-toast";
import { formatCurrency } from "../../../utils/helpers";
import styled from "styled-components";
import { Table } from "../../../components/common/Table";

export const SettlementEligibilityModal = () => {
  const { data: globalData, updateData } = useGlobalContext();
  const { openSettlementEligibilityReview } = globalData || {};
  const [form] = Form.useForm();
  const [filters, setFilters] = useState({ page: 1, page_size: 10 });
  const [formValues, setFormValues] = useState<any>(null);

  const {
    data: beneficiaries,
    loading: beneficiariesLoading,
    loadData: loadBeneficiaries,
  } = useGet();

  const {
    uploadData,
    submitting,
    error: submitError,
    success: submitSuccess,
    data: responseData,
  } = useSet();

  useEffect(() => {
    if (openSettlementEligibilityReview) {
      loadBeneficiaries({ api: API_SETTLEMENT_BENEFICIARIES });
    }
  }, [openSettlementEligibilityReview]);

  const handleClose = () => {
    updateData({ openSettlementEligibilityReview: false });
    form.resetFields();
  };

  function handleSubmit(values: any) {
    setFormValues(values);
    setFilters({ page: 1, page_size: 10 });
    submitEligibilityCheck(values, 1, 10);
  }

  const submitEligibilityCheck = (
    values: any,
    page: number,
    page_size: number,
  ) => {
    const cleanedPayload: any = {
      beneficiary_code: values.beneficiary_code,
      batch_type: values.batch_type,
      currency: "UGX",
      page,
      page_size,
    };
    if (values.payment_date_from) {
      cleanedPayload.payment_date_from = `${values.payment_date_from.format("YYYY-MM-DD")}T00:00:00Z`;
    }
    if (values.payment_date_to) {
      cleanedPayload.payment_date_to = `${values.payment_date_to.format("YYYY-MM-DD")}T23:59:59Z`;
    }
    uploadData({ api: API_SETTLEMENT_ELIGIBILITY, params: cleanedPayload });
  };

  useEffect(() => {
    if (submitSuccess && responseData) {
      toast.success("Settlement eligibility check completed successfully");
    }
  }, [submitSuccess, responseData]);

  const calculateMetrics = () => {
    if (!responseData) return null;

    const results = responseData.results || [];
    const eligible = results.filter((r: any) => r.eligible === true);
    const excluded = results.filter((r: any) => r.eligible === false);

    const eligibleTotal = eligible.reduce(
      (sum: number, r: any) => sum + parseFloat(r.amount || 0),
      0,
    );
    const excludedTotal = excluded.reduce(
      (sum: number, r: any) => sum + parseFloat(r.amount || 0),
      0,
    );

    return {
      transactionsScanned: responseData.count || 0,
      eligibleCount: responseData.eligible_count || eligible.length,
      excludedCount: responseData.excluded_count || excluded.length,
      eligibleTotal,
      excludedTotal,
    };
  };

  const metrics = calculateMetrics();
  const results: any[] = responseData?.results || [];

  const handleFilterChange = (newFilters: any) => {
    setFilters(newFilters);
    if (formValues) {
      submitEligibilityCheck(formValues, newFilters.page, newFilters.page_size);
    }
  };

  useEffect(() => {
    if (
      submitError &&
      Object.keys(submitError).length > 0 &&
      !submitError.notSubmit
    ) {
      const errorMessage =
        (typeof submitError?.message === "string"
          ? submitError.message
          : null) ||
        (typeof submitError?.detail === "string" ? submitError.detail : null) ||
        "Failed to check settlement eligibility";
      toast.error(errorMessage);
    }
  }, [submitError]);

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const REASON_LABELS: Record<string, string> = {
    NOT_RECONCILED: "Not Reconciled",
    MISSING_ESCROW_ID: "Missing Escrow ID",
    NO_SETTLEMENT_RULE: "No Settlement Rule",
  };

  const tableColumns = [
    {
      id: "transaction_id",
      header: "Transaction ID",
      render: (row: any) => <TxId>{row.transaction_id}</TxId>,
    },
    {
      id: "amount",
      header: "Amount (UGX)",
      render: (row: any) => (
        <strong>{formatCurrency(parseFloat(row.amount))}</strong>
      ),
    },
    {
      id: "payment_date",
      header: "Payment Date",
      render: (row: any) => (
        <span style={{ color: "#595959", fontSize: 13 }}>
          {formatDate(row.payment_date)}
        </span>
      ),
    },
    {
      id: "eligible",
      header: "Eligible",
      render: (row: any) =>
        row.eligible ? (
          <StatusBadge $type="eligible">✓ Yes</StatusBadge>
        ) : (
          <StatusBadge $type="excluded">✕ No</StatusBadge>
        ),
    },
    {
      id: "reasons",
      header: "Reasons",
      render: (row: any) => (
        <ReasonsList>
          {row.reasons?.length > 0 ? (
            row.reasons.map((r: string) => (
              <ReasonTag key={r}>{REASON_LABELS[r] || r}</ReasonTag>
            ))
          ) : (
            <span style={{ color: "#d9d9d9" }}>—</span>
          )}
        </ReasonsList>
      ),
    },
  ];

  return (
    <Modal
      open={!!openSettlementEligibilityReview}
      onCancel={handleClose}
      title="Settlement Eligibility Review"
      footer={null}
      centered
      width={920}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        autoComplete="off"
      >
        {/* Filter Section */}
        <FilterCard>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={
                  <FieldLabel>
                    Beneficiary <Required>*</Required>
                  </FieldLabel>
                }
                name="beneficiary_code"
                required={false}
                rules={[
                  {
                    required: true,
                    message: "Please select a beneficiary code",
                  },
                ]}
              >
                <Select
                  placeholder="Select Beneficiary Code"
                  loading={beneficiariesLoading}
                  showSearch
                  optionFilterProp="children"
                >
                  {beneficiaries?.results?.map((b: any) => (
                    <Select.Option key={b.name} value={b.code}>
                      {b.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={
                  <FieldLabel>
                    Batch Type <Required>*</Required>
                  </FieldLabel>
                }
                name="batch_type"
                required={false}
                rules={[
                  { required: true, message: "Please select a batch type" },
                ]}
              >
                <Select placeholder="Select Batch Type">
                  {BENEFICIARY_TYPES.map((type: any) => (
                    <Select.Option key={type.id} value={type.id}>
                      {type.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={
                  <FieldLabel>
                    Payment Date From <Required>*</Required>
                  </FieldLabel>
                }
                name="payment_date_from"
                required={false}
                rules={[
                  {
                    required: true,
                    message: "Please select payment date from",
                  },
                ]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format="DD-MM-YYYY"
                  placeholder="Select start date"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={
                  <FieldLabel>
                    Payment Date To <Required>*</Required>
                  </FieldLabel>
                }
                name="payment_date_to"
                required={false}
                rules={[
                  { required: true, message: "Please select payment date to" },
                ]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format="DD-MM-YYYY"
                  placeholder="Select end date"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ marginBottom: 0, marginTop: 4 }}>
            <ActionRow>
              <Button onClick={handleClose} size="middle">
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
                size="middle"
              >
                Check Eligibility
              </Button>
            </ActionRow>
          </Form.Item>
        </FilterCard>

        {/* Results */}
        {metrics && (
          <>
            {/* Stats row */}
            <StatsRow>
              <StatItem>
                <StatValue>{metrics.transactionsScanned}</StatValue>
                <StatLabel>Transactions Scanned</StatLabel>
              </StatItem>
              <StatSep />
              <StatItem>
                <StatValue $color="#389e0d">{metrics.eligibleCount}</StatValue>
                <StatLabel>Eligible</StatLabel>
              </StatItem>
              <StatSep />
              <StatItem>
                <StatValue $color="#d46b08">{metrics.excludedCount}</StatValue>
                <StatLabel>Excluded</StatLabel>
              </StatItem>
              <StatSep />
              <StatItem>
                <StatValue>{formatCurrency(metrics.excludedTotal)}</StatValue>
                <StatLabel>Excluded Total (UGX)</StatLabel>
              </StatItem>
              <StatSep />
              <StatItem $highlight>
                <StatValue $color="#1677ff">
                  {formatCurrency(metrics.eligibleTotal)}
                </StatValue>
                <StatLabel>Eligible Total (UGX)</StatLabel>
              </StatItem>
            </StatsRow>

            {/* Table */}
            <div style={{ marginTop: "10px" }}>
              <Table
                tableTitle="Transaction Results"
                loading={submitting}
                count={responseData?.count || 0}
                tableColumns={tableColumns}
                tableData={results}
                hideFilters={true}
                filters={filters}
                onFilterChange={handleFilterChange}
                showPagination={true}
                pageItemsSize={10}
              />
            </div>
          </>
        )}
      </Form>
    </Modal>
  );
};

/* ─── Shared ──────────────────────────────────────────────── */
const FieldLabel = styled.span`
  font-size: 12px;
  color: rgba(0, 0, 0, 0.88);
`;
const Required = styled.span`
  color: #ff4d4f;
  margin-left: 2px;
`;

/* ─── Filter card ─────────────────────────────────────────── */
const FilterCard = styled.div`
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  padding: 12px 12px 8px;
  margin-bottom: 10px;
  background: #fff;

  .ant-form-item {
    margin-bottom: 10px;
  }

  .ant-form-item-label {
    padding-bottom: 2px;
  }
`;
const ActionRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

/* ─── Stats row ───────────────────────────────────────────── */
const StatsRow = styled.div`
  display: flex;
  align-items: stretch;
  background: #fff;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  margin-bottom: 10px;
  overflow: hidden;
`;
const StatItem = styled.div<{ $highlight?: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 8px 14px;
  background: ${(p) => (p.$highlight ? "#e6f4ff" : "transparent")};
`;
const StatSep = styled.div`
  width: 1px;
  background: #f0f0f0;
  flex-shrink: 0;
`;
const StatValue = styled.span<{ $color?: string }>`
  font-size: 16px;
  font-weight: 600;
  color: ${(p) => p.$color || "rgba(0,0,0,0.88)"};
  line-height: 1.2;
`;
const StatLabel = styled.span`
  font-size: 11px;
  color: #8c8c8c;
  margin-top: 1px;
`;

/* ─── Table Components ────────────────────────────────────── */
const TxId = styled.code`
  font-size: 12px;
  background: #f5f5f5;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  padding: 1px 6px;
  color: rgba(0, 0, 0, 0.65);
`;
const StatusBadge = styled.span<{ $type: "eligible" | "excluded" }>`
  display: inline-block;
  font-size: 12px;
  font-weight: 500;
  border-radius: 4px;
  padding: 2px 8px;
  ${(p) =>
    p.$type === "eligible"
      ? "background: #f6ffed; color: #389e0d; border: 1px solid #b7eb8f;"
      : "background: #fff2f0; color: #cf1322; border: 1px solid #ffa39e;"}
`;
const ReasonsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;
const ReasonTag = styled.span`
  font-size: 11px;
  color: #874d00;
  background: #fffbe6;
  border: 1px solid #ffe58f;
  border-radius: 4px;
  padding: 1px 7px;
  white-space: nowrap;
`;
